import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useState, type ReactNode } from 'react'
import { mutate } from 'swr'
import type { SWRResponse } from 'swr'

import { Link } from '../lib/Link'
import { api } from '../lib/password'
import DbCard from './DbCard'
import DbOrderHeader from './DbOrderHeader'
import type { FullOrder, FullPlan, FullRecipe, Order, Plan } from './types'

const ORDERS_CONTAINER = 'orders_sorted'

function orderSectionId(orderId: number) {
  return 'order-section-' + orderId
}

function parseOrderSectionId(id: string | number | undefined) {
  if (typeof id !== 'string' || !id.startsWith('order-section-')) return null
  const n = parseInt(id.slice('order-section-'.length), 10)
  return Number.isFinite(n) ? n : null
}

export function DragDrop({
  children,
  home,
  orders,
}: {
  children: ReactNode
  home: SWRResponse<FullPlan[]>
  orders: SWRResponse<FullOrder[]>
}) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event
    if (!over) return

    if (active.data.current?.type === 'order') {
      await handleOrderReorder(event)
      return
    }

    let sourceDroppableId = active.data.current?.sortable.containerId as
      | string
      | undefined
    let destinationDroppableId = over.data.current?.sortable.containerId as
      | string
      | undefined
    let destIndexOverride: number | undefined

    const overSectionId = parseOrderSectionId(String(over.id))
    if (overSectionId != null) {
      destinationDroppableId = 'order-' + overSectionId
      const host = orders.data?.find((o) => o.id === overSectionId)
      destIndexOverride = host?.plans.length ?? 0
    }

    if (!sourceDroppableId || !destinationDroppableId) return
    if (sourceDroppableId === ORDERS_CONTAINER) return

    const homeUpdates: [number, number][] = []
    const orderUpdates: [number, number][] = []

    if (sourceDroppableId === destinationDroppableId) {
      if (sourceDroppableId === 'home_sorted') {
        home.mutate(
          (plans?: FullPlan[]) => {
            if (!plans) return plans
            const oldIndex = active.data.current?.sortable.index
            const newIndex = over.data.current?.sortable.index
            const newPlans = arrayMove(plans, oldIndex, newIndex)

            for (let i = 0; i < newPlans.length; i++) {
              const plan = newPlans[i]
              if (!plan) continue
              if (plan.order !== i) {
                newPlans[i] = { ...plan, order: i }
                homeUpdates.push([plan.id, i])
              }
            }

            return newPlans
          },
          { revalidate: false }
        )
      } else {
        orders.mutate(
          (all?: FullOrder[]) => {
            if (!all) return all
            const orderId = parseInt(sourceDroppableId.split('-')[1]!)
            const orderIndex = all.findIndex((c) => c.id === orderId)
            const order = all[orderIndex]
            if (!order) return all

            const oldIndex = active.data.current?.sortable.index
            const newIndex =
              destIndexOverride ?? over.data.current?.sortable.index
            const newPlans = arrayMove([...order.plans], oldIndex, newIndex)

            const next = [...all]
            next[orderIndex] = {
              ...order,
              plans: newPlans,
            }

            for (let i = 0; i < newPlans.length; i++) {
              const plan = newPlans[i]
              if (!plan) continue
              if (plan.orderPosition !== i) {
                newPlans[i] = { ...plan, orderPosition: i }
                orderUpdates.push([plan.id, i])
              }
            }

            return next
          },
          { revalidate: false }
        )
      }
    } else {
      const sourceOrderId =
        sourceDroppableId === 'home_sorted'
          ? undefined
          : parseInt(sourceDroppableId.split('-')[1]!)
      const destinationOrderId =
        destinationDroppableId === 'home_sorted'
          ? null
          : parseInt(destinationDroppableId.split('-')[1]!) || undefined

      const sourceArray =
        sourceDroppableId === 'home_sorted'
          ? home.data
          : orders.data?.find((c) => c.id === sourceOrderId)?.plans

      const destinationArray =
        destinationDroppableId === 'home_sorted'
          ? home.data
          : orders.data?.find((c) => c.id === destinationOrderId)?.plans

      if (!sourceArray || !destinationArray) return

      const sourceIndex = active.data.current?.sortable.index as number
      const destIndex =
        destIndexOverride ?? (over.data.current?.sortable.index as number)
      const [removed] = sourceArray.splice(sourceIndex, 1)
      if (!removed) return
      destinationArray.splice(destIndex, 0, removed)

      home.mutate((a: FullPlan[] | undefined) => [...a!], { revalidate: false })
      orders.mutate((a: FullOrder[] | undefined) => [...a!], {
        revalidate: false,
      })

      await api.patch('/api/plans/' + removed.id, {
        orderId: destinationOrderId,
        orderPosition: destinationOrderId ? destIndex : undefined,
        order: destinationOrderId ? null : destIndex,
      })

      for (let i = destIndex; i < destinationArray.length; i++) {
        const plan = destinationArray[i]
        if (!plan) continue
        if (destinationOrderId) orderUpdates.push([plan.id, i])
        else homeUpdates.push([plan.id, i])
      }

      if (sourceOrderId) {
        mutate('/api/orders/' + sourceOrderId)
      }
      if (destinationOrderId) {
        mutate('/api/orders/' + destinationOrderId)
      }
    }

    await Promise.all([
      ...homeUpdates.map(([id, order]) =>
        api.patch('/api/plans/' + id, { order })
      ),
      ...orderUpdates.map(([id, orderPosition]) =>
        api.patch('/api/plans/' + id, { orderPosition })
      ),
    ])
    if (homeUpdates.length) home.mutate()
    if (orderUpdates.length) orders.mutate()
  }

  async function handleOrderReorder(event: DragEndEvent) {
    const { active, over } = event
    if (!over || !orders.data) return

    const activeOrderId = parseOrderSectionId(String(active.id))
    let overOrderId = parseOrderSectionId(String(over.id))

    if (overOrderId == null) {
      const overType = over.data.current?.type
      if (overType === 'order' && typeof over.data.current?.orderId === 'number') {
        overOrderId = over.data.current.orderId
      } else if (String(over.id).startsWith('plan-')) {
        const planId = parseInt(String(over.id).slice(5), 10)
        overOrderId =
          orders.data.find((o) => o.plans.some((p) => p.id === planId))?.id ??
          null
      } else {
        const containerId = over.data.current?.sortable?.containerId as
          | string
          | undefined
        if (containerId?.startsWith('order-')) {
          overOrderId = parseInt(containerId.slice(6), 10) || null
        }
      }
    }

    if (activeOrderId == null || overOrderId == null) return
    if (activeOrderId === overOrderId) return

    const oldIndex = orders.data.findIndex((o) => o.id === activeOrderId)
    const newIndex = orders.data.findIndex((o) => o.id === overOrderId)
    if (oldIndex < 0 || newIndex < 0) return

    const next = arrayMove(orders.data, oldIndex, newIndex).map((o, i) => ({
      ...o,
      position: i,
    }))
    orders.mutate(next, { revalidate: false })
    try {
      await api.patch('/api/orders', {
        orderIds: next.map((o) => o.id),
      })
    } finally {
      orders.mutate()
    }
  }

  const activePlan =
    home.data?.find((p) => 'plan-' + p.id === activeId) ||
    orders.data
      ?.flatMap((c) => c.plans)
      .find((p) => 'plan-' + p.id === activeId)

  const activeOrder =
    activeId != null
      ? orders.data?.find((o) => orderSectionId(o.id) === activeId)
      : undefined

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}
      <DragOverlay>
        {activeId && activeOrder ? (
          <div className="bg-green-800/90 text-white rounded-lg px-4 py-3 font-semibold text-2xl opacity-90 shadow-lg cursor-grabbing">
            {activeOrder.display || 'Bestelling'}
          </div>
        ) : activeId && activePlan ? (
          <div className="bg-green-800 text-white rounded-lg">
            <DbCard plan={activePlan} recipe={activePlan.recipe} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

/** Sortable shopping list column; drag from the title to reorder lists. */
export function SortableOrderSection({
  order,
  summary,
  children,
}: {
  order: FullOrder & Partial<Order>
  summary?: ReactNode
  children: ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: orderSectionId(order.id),
    data: {
      type: 'order',
      orderId: order.id,
    },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
  }

  return (
    <section
      ref={setNodeRef}
      style={style}
      className={`mb-10 select-none last:mb-6 ${isDragging ? 'z-10' : ''}`}
    >
      <DbOrderHeader
        order={order}
        summary={summary}
        dragHandle={{
          attributes,
          listeners,
          setActivatorNodeRef,
        }}
      />
      {children}
    </section>
  )
}

export function SortableOrders({
  orders,
  children,
}: {
  orders: { id: number }[]
  children: ReactNode
}) {
  return (
    <SortableContext
      items={orders.map((o) => orderSectionId(o.id))}
      strategy={verticalListSortingStrategy}
      id={ORDERS_CONTAINER}
    >
      {children}
    </SortableContext>
  )
}

export function SortablePlanList({
  orderId,
  plans,
  isDropDisabled,
}: {
  orderId?: number
  plans?: (Plan & { recipe: FullRecipe | null })[]
  isDropDisabled?: boolean
}) {
  const empty = !plans?.length
  const droppableId = orderId
    ? 'order-' + orderId
    : isDropDisabled
      ? 'home_unsorted'
      : 'home_sorted'

  return (
    <SortableContext
      items={plans?.map((plan) => 'plan-' + plan.id) || []}
      strategy={verticalListSortingStrategy}
      id={droppableId}
    >
      {empty ? (
        <Placeholder key="placeholder" />
      ) : (
        plans?.map((plan) => (
          <DraggablePlan
            key={plan.id}
            plan={plan}
            index={orderId ? plan.orderPosition : plan.order}
          />
        ))
      )}
    </SortableContext>
  )
}

export function DraggablePlan({
  plan,
  index,
}: {
  plan: Plan & { index?: number; recipe: FullRecipe | null }
  index: number | null
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: 'plan-' + plan.id,
    data: {
      type: 'plan',
      plan,
    },
  })

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        transition,
        opacity: isDragging ? 0.4 : 1,
        touchAction: 'none',
      }
    : undefined

  return (
    <Link
      href={{ query: { plan: plan.id } }}
      key={plan.id}
      className={`block group focus:outline-hidden mb-6 relative touch-manipulation ${
        isDragging ? 'z-10' : ''
      }`}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <DbCard plan={plan} recipe={plan.recipe} />
    </Link>
  )
}

const Placeholder = () => {
  const { setNodeRef } = useSortable({ id: 'placeholder' })
  return (
    <div
      className="text-gray-400 text-center"
      id="placeholder"
      ref={setNodeRef}
    >
      <div className="text-white text-sm bg-green-700 p-4 rounded-lg">
        Voeg een recept toe met <b>+</b> of sleep een recept
      </div>
    </div>
  )
}
